// productionMilestonesModalComponents/editGoals.js
//
// "Edit Goals" modal — single-page flat list → slide-in edit panel.
//
// Layout:
//   - Goals List: flat list of all goals, no section headers or category prefixes.
//     Clicking any row slides in the Edit Panel.
//   - Edit Panel: slides in from the right. Contains:
//       · WeeklyEditor  — week-nav + Roll Leader field + Mon–Fri roll/wrap table
//       · YtdEditor     — Jan–Dec monthly completion table
//       · SingleGoalEditor — one numeric input for a production goal
//     Each edit panel includes its own Save / Discard footer.
//
// Number inputs use type="text" with inputMode="decimal" so the user can type
// "-" directly without needing the stepper arrows (a native type="number" field
// swallows intermediate "-" keystrokes on many browsers).
//
// The Roll Leader input uses the same blur-flush pattern as GoalNumberInput:
// a local display string is kept in state while the field is focused so that
// intermediate keystrokes are never reset by the hook's toStringOrNull coercion.
//
// Hook contracts — useEditGoals owns read/draft state (including lineLeader),
// useUpdateGoals owns the POST. commitDraft() is called on success.

import React, { useState, useEffect, useRef, useCallback } from "react";
import useEditGoals from "../../../hooks/useEditGoals";
import useUpdateGoals from "../../../hooks/useUpdateGoals";
import "../styles/editGoals.css";

// ─── Utility helpers ──────────────────────────────────────────────────────────

const todayDateString = () => new Date().toISOString().slice(0, 10);

const shiftDateString = (dateStr, deltaDays) => {
  const base = new Date(`${dateStr || todayDateString()}T00:00:00Z`);
  const shifted = new Date(base.getTime() + deltaDays * 86_400_000);
  return shifted.toISOString().slice(0, 10);
};

const formatShortDate = (isoDate) => {
  if (!isoDate) return "—";
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const formatDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// ─── Numeric text input ───────────────────────────────────────────────────────
//
// Keeps a local display string while focused so intermediate characters like
// "-" and "." are never clobbered by the hook's toNumberOrZero coercion.
// Flushes to the hook on blur only.

const GoalNumberInput = ({
  value,
  onChange,
  ariaLabel,
  disabled,
  className = "",
}) => {
  const [displayValue, setDisplayValue] = useState(String(value ?? ""));
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) {
      setDisplayValue(String(value ?? ""));
    }
  }, [value]);

  const handleFocus = () => {
    isFocused.current = true;
    setDisplayValue(String(value ?? ""));
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    if (
      raw !== "" &&
      !/^-?(\d+\.?\d*|\.?\d*)$/.test(raw) &&
      raw !== "-" &&
      raw !== "."
    )
      return;
    setDisplayValue(raw);
  };

  const handleBlur = () => {
    isFocused.current = false;
    onChange(displayValue);
    const n = Number(displayValue);
    setDisplayValue(String(Number.isFinite(n) ? n : 0));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={`eg-table-input ${className}`.trim()}
      value={displayValue}
      aria-label={ariaLabel}
      disabled={disabled}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

// ─── Roll Leader text input ───────────────────────────────────────────────────
//
// Same blur-flush pattern as GoalNumberInput, adapted for a plain text field.
// The hook's updateLineLeader coerces empty/whitespace to null on flush;
// while the field is focused the raw display string is preserved so the user
// can edit freely without intermediate values being normalised away.
//
// prop `value`    — string | null from draftWeeklyRollGoals.lineLeader
// prop `onChange` — updateLineLeader from useEditGoals

const LineLeaderInput = ({ value, onChange, disabled }) => {
  const [displayValue, setDisplayValue] = useState(value ?? "");
  const isFocused = useRef(false);

  // Sync display when the prop changes externally (Discard, week navigation).
  // Guard against overwriting an active edit with an incoming prop update.
  useEffect(() => {
    if (!isFocused.current) {
      setDisplayValue(value ?? "");
    }
  }, [value]);

  const handleFocus = () => {
    isFocused.current = true;
    setDisplayValue(value ?? "");
  };

  const handleChange = (e) => {
    setDisplayValue(e.target.value);
  };

  const handleBlur = () => {
    isFocused.current = false;
    // Flush trimmed string (or empty → hook converts to null) to the hook.
    onChange(displayValue);
    // Normalise display: if the hook will store null, show "" not " ".
    setDisplayValue(displayValue.trim());
  };

  return (
    <input
      type="text"
      className="eg-table-input eg-leader-input"
      value={displayValue}
      placeholder="e.g. Jane Smith"
      aria-label="Roll Leader for this week"
      disabled={disabled}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      autoComplete="off"
      spellCheck={false}
    />
  );
};

// ─── Goal list row ────────────────────────────────────────────────────────────

const GoalListItem = ({ label, currentValue, isDirty, onClick }) => (
  <button type="button" className="eg-list-item" onClick={onClick}>
    <span className="eg-list-item__name">
      {label}
      {isDirty && <span className="eg-list-item__dirty" aria-hidden="true" />}
    </span>
    <span className="eg-list-item__value">{currentValue}</span>
    <span className="eg-list-item__chevron" aria-hidden="true">
      ›
    </span>
  </button>
);

// ─── Actions footer ───────────────────────────────────────────────────────────

const ActionsFooter = ({
  statusNode,
  isDirty,
  saving,
  onDiscard,
  onSave,
  validationErrors,
}) => (
  <div className="eg-actions-wrap">
    {validationErrors && validationErrors.length > 0 && (
      <ul className="eg-validation-list">
        {validationErrors.map((ve, idx) => (
          <li key={`${ve.where}-${ve.field}-${idx}`}>
            <strong>{ve.where}</strong> — {ve.message}
          </li>
        ))}
      </ul>
    )}
    <div className="eg-actions">
      {statusNode}
      <button
        type="button"
        className="eg-btn eg-btn--secondary"
        onClick={onDiscard}
        disabled={!isDirty || saving}
      >
        Discard
      </button>
      <button
        type="button"
        className="eg-btn eg-btn--primary"
        onClick={onSave}
        disabled={!isDirty || saving}
      >
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>
  </div>
);

// ─── Weekly table editor ──────────────────────────────────────────────────────
//
// CHANGE: accepts updateLineLeader prop and renders a Roll Leader field above
// the week-nav bar. The field is scoped to the week currently being edited —
// navigating to a different week via goToWeek() replaces the entire
// draftWeeklyRollGoals object (including lineLeader) so the field resets
// automatically to the new week's leader without any special cleanup.

const WeeklyEditor = ({
  draftWeeklyRollGoals,
  weekDate,
  loadingWeek,
  weekError,
  goToWeek,
  updateWeeklyRollGoal,
  updateLineLeader, // ← NEW
}) => (
  <div className="eg-edit-content">
    {/* ── Week navigation + Roll Leader inline ────────────────────────────── */}
    <div className="eg-week-filter">
      <button
        type="button"
        className="eg-week-nav-btn"
        onClick={() => goToWeek(shiftDateString(weekDate, -7))}
        aria-label="Previous week"
      >
        ‹ Prev
      </button>

      <input
        type="date"
        className="eg-week-date-input"
        value={weekDate}
        onChange={(e) => e.target.value && goToWeek(e.target.value)}
        aria-label="Jump to a date within the week"
      />

      <button
        type="button"
        className="eg-week-nav-btn"
        onClick={() => goToWeek(shiftDateString(weekDate, 7))}
        aria-label="Next week"
      >
        Next ›
      </button>

      <button
        type="button"
        className="eg-week-nav-btn eg-week-nav-btn--reset"
        onClick={() => goToWeek(todayDateString())}
      >
        This Week
      </button>

      {loadingWeek && <span className="eg-week-filter-status">Loading…</span>}
      {weekError && (
        <span className="eg-week-filter-status eg-week-filter-status--error">
          {weekError}
        </span>
      )}

      {/* Roll Leader — inline with the date controls */}
      <label className="eg-leader-label" htmlFor="eg-leader-input">
        Roll Leader
      </label>
      <LineLeaderInput
        value={draftWeeklyRollGoals.lineLeader}
        onChange={updateLineLeader}
        disabled={loadingWeek}
      />
    </div>

    <p className="eg-section-subtitle">
      Week of {formatShortDate(draftWeeklyRollGoals.weekStart)}
      {" – "}
      {formatShortDate(draftWeeklyRollGoals.weekEnd)}
    </p>

    <table className="modal-table eg-modal-table">
      <thead>
        <tr>
          <th>Day</th>
          <th>Roll Goal</th>
          <th>Wrap Goal</th>
        </tr>
      </thead>
      <tbody>
        {draftWeeklyRollGoals.days.length === 0 ? (
          <tr>
            <td colSpan={3} className="modal-table-empty">
              No data for this week.
            </td>
          </tr>
        ) : (
          draftWeeklyRollGoals.days.map((day) => (
            <tr key={day.date}>
              <td className="eg-day-cell">
                <span className="eg-day-label">{day.dayLabel}</span>
                <span className="eg-day-date">{formatShortDate(day.date)}</span>
              </td>
              <td>
                <GoalNumberInput
                  value={day.roll}
                  ariaLabel={`Roll goal for ${day.dayLabel}`}
                  onChange={(val) =>
                    updateWeeklyRollGoal(day.date, "roll", val)
                  }
                />
              </td>
              <td>
                <GoalNumberInput
                  value={day.wrap}
                  ariaLabel={`Wrap goal for ${day.dayLabel}`}
                  onChange={(val) =>
                    updateWeeklyRollGoal(day.date, "wrap", val)
                  }
                />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ─── YTD monthly table editor ─────────────────────────────────────────────────

const YtdEditor = ({ draftYtdCompletionGoals, updateYtdGoal }) => (
  <div className="eg-edit-content">
    <p className="eg-section-subtitle">
      Monthly completion targets for {draftYtdCompletionGoals.year}
    </p>

    <table className="modal-table eg-modal-table eg-ytd-table">
      <thead>
        <tr>
          <th className="eg-col-month">Month</th>
          <th>Goal Value</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {draftYtdCompletionGoals.months.length === 0 ? (
          <tr>
            <td colSpan={3} className="modal-table-empty">
              No monthly data available.
            </td>
          </tr>
        ) : (
          draftYtdCompletionGoals.months.map((m) => (
            <tr key={m.monthDate}>
              <td className="eg-col-month eg-month-label">{m.month}</td>
              <td>
                <GoalNumberInput
                  value={m.goalValue}
                  ariaLabel={`YTD goal for ${m.month}`}
                  onChange={(val) => updateYtdGoal(m.monthDate, val)}
                />
              </td>
              <td>
                {!m.hasGoal ? (
                  <span className="eg-no-goal-tag">New</span>
                ) : (
                  <span className="eg-set-tag">Set</span>
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ─── Single numeric goal editor ───────────────────────────────────────────────
const SuffixedInput = ({ children, suffix }) => (
  <div className="eg-suffixed-input">
    {children}
    <span className="eg-input-suffix">{suffix}</span>
  </div>
);

const SingleGoalEditor = ({ goal, onUpdate }) => {
  const unitType = goal.unitType ?? "numeric";

  const label =
    unitType === "percent"
      ? "Goal Value (%)"
      : unitType === "numeric-thousand"
        ? "Goal Value (full number)"
        : "Goal Value";

  const hint =
    unitType === "percent"
      ? "Enter a whole number between 0 and 100 (e.g. 85 for 85%)."
      : unitType === "numeric-thousand"
        ? "Enter the full value in any magnitude (e.g. 12500). Stored as thousands (12.5k) in SQL."
        : "Supports positive, negative, and decimal values.";

  const inputNode = (
    <GoalNumberInput
      value={goal.goalValue}
      ariaLabel={`Goal value for ${goal.goalName}`}
      className="eg-detail-input"
      onChange={(val) => onUpdate(goal.goalId, val)}
    />
  );

  return (
    <div className="eg-edit-content eg-edit-content--single">
      {goal.updatedAt && (
        <p className="eg-detail-meta">
          Last updated: {formatDateTime(goal.updatedAt)}
        </p>
      )}

      <label className="eg-detail-label">{label}</label>

      {unitType === "percent" ? (
        <SuffixedInput suffix="%">{inputNode}</SuffixedInput>
      ) : unitType === "numeric-thousand" ? (
        <SuffixedInput suffix="k">{inputNode}</SuffixedInput>
      ) : (
        inputNode
      )}

      <p className="eg-detail-hint">{hint}</p>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const EditGoals = () => {
  const {
    weeklyRollGoals,
    productionGoals,
    ytdCompletionGoals,
    draftWeeklyRollGoals,
    draftProductionGoals,
    draftYtdCompletionGoals,
    loading,
    error,
    isDirty,
    weekDate,
    loadingWeek,
    weekError,
    goToWeek,
    updateWeeklyRollGoal,
    updateLineLeader, // ← NEW
    updateProductionGoal,
    updateYtdGoal,
    resetDraft,
    commitDraft,
  } = useEditGoals();

  const {
    saving,
    saveError,
    validationErrors,
    lastSavedAt,
    updateGoals,
    clearError,
  } = useUpdateGoals();

  // null → Goals List   |   { type, goalId? } → Edit Panel
  const [activeGoal, setActiveGoal] = useState(null);
  // "in" | "out" | null — drives the slide animation class
  const [panelAnim, setPanelAnim] = useState(null);

  // Clear save error whenever the draft changes.
  useEffect(() => {
    if (isDirty) clearError();
  }, [isDirty, clearError]);

  // ── Navigation ───────────────────────────────────────────────────────────────

  const openGoal = useCallback((goal) => {
    setActiveGoal(goal);
    setPanelAnim("in");
  }, []);

  const closePanel = useCallback(() => {
    setPanelAnim("out");
    setTimeout(() => {
      setActiveGoal(null);
      setPanelAnim(null);
    }, 260);
  }, []);

  // ── Save / discard ───────────────────────────────────────────────────────────
  //
  // draftWeeklyRollGoals is passed as-is — it now carries lineLeader at the
  // top level, which dataPoster.js / dataPost.js read from weeklyRollGoals.
  // No reshaping needed here; the draft object shape matches the POST contract.

  const handleSave = async () => {
    const payload = {
      weeklyRollGoals: draftWeeklyRollGoals,
      productionGoals: draftProductionGoals,
      ytdCompletionGoals: draftYtdCompletionGoals,
    };
    const result = await updateGoals(payload);
    if (result.ok) {
      commitDraft(result.updated);
    }
  };

  const handleDiscard = () => {
    resetDraft();
    clearError();
  };

  // ── Status node ───────────────────────────────────────────────────────────────

  let statusNode = null;
  if (saving) {
    statusNode = <span className="eg-status">Saving…</span>;
  } else if (saveError) {
    statusNode = (
      <span className="eg-status eg-status--error">{saveError}</span>
    );
  } else if (lastSavedAt && !isDirty) {
    statusNode = <span className="eg-status eg-status--saved">Saved.</span>;
  } else if (isDirty) {
    statusNode = (
      <span className="eg-status eg-status--dirty">Unsaved changes</span>
    );
  }

  // ── Per-goal dirty checks for the list dots ───────────────────────────────────

  const isWeeklyDirty =
    JSON.stringify(weeklyRollGoals) !== JSON.stringify(draftWeeklyRollGoals);
  const isYtdDirty =
    JSON.stringify(ytdCompletionGoals) !==
    JSON.stringify(draftYtdCompletionGoals);
  const isProdDirtyFor = (goalId) => {
    const server = productionGoals.find((g) => g.goalId === goalId);
    const draftGoal = draftProductionGoals.find((g) => g.goalId === goalId);
    return !!(server && draftGoal && server.goalValue !== draftGoal.goalValue);
  };

  // ── Resolve active production goal draft ──────────────────────────────────────

  const activeProductionGoal =
    activeGoal?.type === "production"
      ? (draftProductionGoals.find((g) => g.goalId === activeGoal.goalId) ??
        null)
      : null;

  // ── List-item summary values ──────────────────────────────────────────────────
  //
  // weekRollSummary: shows roll/wrap averages + leader name when set.

  const weekRollSummary = (() => {
    const days = draftWeeklyRollGoals.days;
    if (!days.length) return "No data";
    const avg = (arr) =>
      Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);
    const base = `Avg roll ${avg(days.map((d) => d.roll))} · wrap ${avg(days.map((d) => d.wrap))}`;
    const leader = draftWeeklyRollGoals.lineLeader;
    return leader ? `${base} · ${leader}` : base;
  })();

  const ytdSummary = (() => {
    const months = draftYtdCompletionGoals.months;
    const set = months.filter((m) => m.hasGoal).length;
    return `${set} / ${months.length} months set`;
  })();

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <>
      <p className="modal-description">
        Update roll/wrap targets, production goals, and monthly YTD completion
        goals.
      </p>

      {loading && <p className="modal-loading">Loading goals…</p>}
      {error && (
        <p className="modal-error">Unable to load goals. Please try again.</p>
      )}

      {!loading && !error && (
        <div className="eg-root">
          {/* ── Goals List ──────────────────────────────────────────────────── */}
          <div
            className={`eg-list-view${activeGoal ? " eg-list-view--hidden" : ""}`}
            aria-hidden={!!activeGoal}
          >
            <div className="eg-list-flat">
              <GoalListItem
                label="Weekly Module Rolls & Wraps"
                currentValue={weekRollSummary}
                isDirty={isWeeklyDirty}
                onClick={() => openGoal({ type: "weekly" })}
              />

              {draftProductionGoals.map((g) => (
                <GoalListItem
                  key={g.goalId}
                  label={g.goalName}
                  currentValue={g.goalValue}
                  isDirty={isProdDirtyFor(g.goalId)}
                  onClick={() =>
                    openGoal({ type: "production", goalId: g.goalId })
                  }
                />
              ))}

              <GoalListItem
                label="YTD Completion Monthly Targets"
                currentValue={ytdSummary}
                isDirty={isYtdDirty}
                onClick={() => openGoal({ type: "ytd" })}
              />
            </div>

            <ActionsFooter
              statusNode={statusNode}
              isDirty={isDirty}
              saving={saving}
              onDiscard={handleDiscard}
              onSave={handleSave}
              validationErrors={validationErrors}
            />
          </div>

          {/* ── Edit Panel ──────────────────────────────────────────────────── */}
          {activeGoal && (
            <div
              className={`eg-edit-panel${
                panelAnim === "in"
                  ? " eg-edit-panel--enter"
                  : panelAnim === "out"
                    ? " eg-edit-panel--exit"
                    : ""
              }`}
            >
              {/* Panel header */}
              <div className="eg-edit-header">
                <button
                  type="button"
                  className="eg-back-btn"
                  onClick={closePanel}
                  aria-label="Back to goals list"
                >
                  ‹ Back
                </button>

                <div className="eg-edit-header__titles">
                  <h3 className="eg-edit-header__title">
                    {activeGoal.type === "weekly" &&
                      "Weekly Module Rolls & Wraps"}
                    {activeGoal.type === "production" &&
                      activeProductionGoal?.goalName}
                    {activeGoal.type === "ytd" &&
                      `YTD Completion Monthly Targets ${draftYtdCompletionGoals.year}`}
                  </h3>
                </div>
              </div>

              {/* Scrollable body */}
              {activeGoal.type === "weekly" && (
                <WeeklyEditor
                  draftWeeklyRollGoals={draftWeeklyRollGoals}
                  weekDate={weekDate}
                  loadingWeek={loadingWeek}
                  weekError={weekError}
                  goToWeek={goToWeek}
                  updateWeeklyRollGoal={updateWeeklyRollGoal}
                  updateLineLeader={updateLineLeader}
                />
              )}

              {activeGoal.type === "ytd" && (
                <YtdEditor
                  draftYtdCompletionGoals={draftYtdCompletionGoals}
                  updateYtdGoal={updateYtdGoal}
                />
              )}

              {activeGoal.type === "production" && activeProductionGoal && (
                <SingleGoalEditor
                  goal={activeProductionGoal}
                  onUpdate={updateProductionGoal}
                />
              )}

              <ActionsFooter
                statusNode={statusNode}
                isDirty={isDirty}
                saving={saving}
                onDiscard={handleDiscard}
                onSave={handleSave}
                validationErrors={validationErrors}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default EditGoals;
